import type {
  ApiError,
  SdkTwoFactorSetupHandle,
  SdkUseTwoFactorOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  isRestartError,
  navigateAfterSignIn,
  postTwoFactor,
  type SetupProps,
  storeTwoFactorFlow,
  type TwoFactorCompletion,
  twoFactorFlowQueryKey,
  twoFactorFlowUrl,
  useCancelPendingSignIn,
  useSetupQuery,
  useTwoFactorTarget
} from './twoFactorFlows';

type SetupOperation =
  | { operation: 'authorize'; code: string }
  | { operation: 'authorize'; recovery_code: string }
  | { operation: 'phone'; phone: string }
  | { operation: 'verify'; code: string }
  | { operation: 'finish' }
  | { operation: 'cancel' };

type SetupResponse = SetupProps | TwoFactorCompletion | '';

function isCompletion(response: SetupResponse): response is TwoFactorCompletion {
  return typeof response === 'object' && 'next' in response;
}

export function useTwoFactorSetup(options?: SdkUseTwoFactorOptions): SdkTwoFactorSetupHandle {
  const target = useTwoFactorTarget(options?.resourceId);
  const resourceId = target.resourceId;
  const isPendingSetup =
    target.pending !== null &&
    target.pending.stage === 'setup' &&
    target.pending.resourceId === resourceId;
  const isActive =
    isPendingSetup || (target.signedInResourceId === resourceId && target.twoFactorEnabled);
  const queryClient = useQueryClient();
  const query = useSetupQuery(resourceId, isActive);
  const cancelSignIn = useCancelPendingSignIn(resourceId);
  const [codes, setCodes] = useState<string[] | null>(null);

  const operation = useMutation<SetupResponse, ApiError, SetupOperation>({
    mutationFn: (fields) => {
      if (resourceId === null) {
        throw new Error('A two-factor setup needs a resource.');
      }

      return postTwoFactor(twoFactorFlowUrl('setup', resourceId), fields);
    },
    onSuccess: (response, fields) => {
      if (isCompletion(response)) {
        navigateAfterSignIn(response.url);

        return;
      }

      if (resourceId === null) {
        return;
      }

      // A proof can spend a backup code, and a new number changes what the backup codes
      // panel shows, so its state is read again.
      if (fields.operation !== 'phone' && fields.operation !== 'cancel') {
        void queryClient.invalidateQueries({
          queryKey: twoFactorFlowQueryKey('backupCodes', resourceId)
        });
      }

      if (response === '') {
        // A finished or stopped replacement has no state left; the next load starts over.
        setCodes(null);
        void queryClient.invalidateQueries({
          queryKey: twoFactorFlowQueryKey('setup', resourceId)
        });

        return;
      }

      if (fields.operation === 'verify') {
        setCodes(response.codes ?? null);
      }

      storeTwoFactorFlow(queryClient, 'setup', resourceId, response);
    }
  });

  const run = (fields: SetupOperation) =>
    operation.mutateAsync(fields).then(
      () => true,
      () => false
    );

  const props = query.data;
  const actionError = operation.error ?? cancelSignIn.error;

  return {
    isActive,
    data: props && {
      step: props.step,
      isReplacement: props.isReplacement,
      maskedPhone: props.maskedPhone,
      status: props.status ?? null
    },
    codes,
    isLoading: isActive && query.isLoading,
    error: query.error,
    isPending: operation.isPending || cancelSignIn.isPending,
    actionError,
    restartRequired: isRestartError(query.error) || isRestartError(actionError),
    verifyExistingFactor: (factor) =>
      run(
        'code' in factor
          ? { operation: 'authorize', code: factor.code }
          : { operation: 'authorize', recovery_code: factor.recoveryCode.trim() }
      ),
    submitPhone: (phone) => run({ operation: 'phone', phone }),
    verifyNewNumber: (code) => run({ operation: 'verify', code }),
    finish: () => run({ operation: 'finish' }),
    cancel: () =>
      isPendingSetup
        ? cancelSignIn.mutateAsync().then(
            () => undefined,
            () => undefined
          )
        : run({ operation: 'cancel' }).then(() => undefined)
  };
}
